import { createContext, useContext, useState, useRef, useCallback, useMemo } from "react";
import { executeWorkflowStep } from "../../services/api/Api";

type FormV3ContextData = {
   formV3Data: any;
   fieldsInfo: any;
   tableRefreshList: any;
   changeFieldsInfo: (formGUID: string, infoObject: any) => void;
   setFormV3Data: (formV3Data: any) => void;
   setTableRefreshList: (formV3Data: any) => void;
   changeFieldValue: (formGUID: string, fieldId: string, value: any) => void;
   addNewFormV3Data: (newFormData: any, formResponseData: any, formGUID: string) => void;
   goToTheNextProcessStep: (
      actionId: any,
      comment: any,
      recipientId: any,
      stepId: any,
      guid: any,
   ) => void;
   getFormV3Locks: (formGUID: string) => void;
   getFormFieldsByGUID: (formGUID: string) => any[];
};

export const FormV3Context = createContext<FormV3ContextData>({
   formV3Data: {},
   fieldsInfo: {},
   tableRefreshList: {},
   changeFieldsInfo: () => {},
   setFormV3Data: () => {},
   setTableRefreshList: () => {},
   changeFieldValue: () => {},
   addNewFormV3Data: () => {},
   goToTheNextProcessStep: () => {},
   getFormV3Locks: () => {},
   getFormFieldsByGUID: () => [],
});

const FormV3Provider = ({ children }) => {
   const [formV3Data, setFormV3Data] = useState({});
   const [formV3Responses, setFormV3Responses] = useState({});
   const formV3DataRef = useRef({});
   const [fieldsInfo, setFieldsInfo] = useState({});
   const [orderList, setOrderList] = useState<string[]>([]);
   const [tableRefreshList, setTableRefreshList] = useState({});

   /**
    * Adds new form data object to context, dataForms are limited up to 5. The Oldest ones are removed when limit is exceeded.
    * @param {string} newFormData - {formGUID:{fieldId:value, fieldId:value, ...}}
    */
   const addNewFormV3Data = useCallback((newFormData, formResponseData, formGUID) => {
      // We limit remembered forms up to 5 because in the future we will also:
      // TODO - set these 5 formData to redux persist as backup so in case of losing data we could restore it
      /* if(Object.keys(formV3DataRef.current) && Object.keys(formV3DataRef.current).length > 4) {
         let reducedData = {...formV3DataRef.current}
         delete reducedData[orderList[0]];
         // Remove first form from orderList to add new one
         const reducedList = [...orderList.reverse()];
         reducedList.pop();
         reducedList.reverse().push(Object.keys(newFormData)[0]);
         setOrderList(reducedList);
         formV3DataRef.current = {...reducedData, ...newFormData};
         setFormV3Data({...reducedData, ...newFormData});
      } else { */
      setOrderList((prev) => [...prev, Object.keys(newFormData)[0]]);
      formV3DataRef.current = { ...formV3DataRef.current, ...newFormData };
      setFormV3Data({ ...formV3DataRef.current, ...newFormData });
      setFormV3Responses((prev) => ({ ...prev, [formGUID]: formResponseData }));
   }, []);

   const getFormV3Locks = useCallback(
      (GUID) => {
         const formAlerts = fieldsInfo[GUID];
         const formFields = formV3Responses[GUID]?.fields;
         const formData = formV3Data[GUID];
         let anyFieldRequiredAndEmpty = false;
         formFields?.forEach((field) => {
            const constraints = field.modelField?.constraints;
            const isRequired = constraints && constraints.some((el) => el === "Required");
            if (isRequired && (formData[field.id] === "" || formData[field.id] === null)) {
               anyFieldRequiredAndEmpty = true;
            }
         });
         const actionLocks = {
            alertLock: formAlerts && Object.keys(formAlerts)?.length > 0 ? true : false,
            requiredLock: anyFieldRequiredAndEmpty,
         };
         return actionLocks;
      },
      [fieldsInfo, formV3Responses, formV3Data],
   );

   /**
    * Changes field value in context and refreshes the field element
    * @param {string} formGUID - Unique FormController id
    * @param {string} fieldId - Field id from java api
    * @param {*} value - New value
    */
   const changeFieldValue = useCallback((formGUID: string, fieldId: string, value: any) => {
      const tempFormV3Data = { ...formV3DataRef.current };
      const formControllerData = tempFormV3Data[formGUID];
      if (formControllerData) {
         formControllerData[fieldId] = value;
         formV3DataRef.current = tempFormV3Data;
         setFormV3Data((prevData) => ({
            ...prevData,
            [formGUID]: {
               ...prevData[formGUID],
               [fieldId]: value,
            },
         }));
      }
   }, []);

   const changeFieldsInfo = useCallback((formGUID: string, infoObject: any) => {
      setFieldsInfo((prev) => {
         const newFieldsInfo = prev ? { ...prev } : {};
         newFieldsInfo[formGUID] = infoObject;
         return newFieldsInfo;
      });
   }, []);

   const goToTheNextProcessStep = useCallback(
      (actionId, comment, recipientId, stepId, guid) => {
         return new Promise(async (resolve, reject) => {
            try {
               let postData = { ...formV3Data[guid] };
               const stepResponse = await executeWorkflowStep(
                  actionId,
                  postData,
                  comment,
                  recipientId,
                  stepId,
               );
               const respData = stepResponse?.data?.action;
               if (
                  JSON.stringify(respData)?.includes(
                     "api.workflow.actions.validation.form_field_required",
                  )
               ) {
                  const infoObject = {};
                  Object.keys(respData).forEach((key) => {
                     const validationError = respData[key];
                     const hasAlert = validationError.hasOwnProperty("hasAlert");
                     const alertContent = validationError.hasAlert;
                     const maskError = validationError.hasOwnProperty("mask");
                     const isRequired = validationError.hasOwnProperty("isRequired");
                     if (hasAlert && alertContent) {
                        infoObject[key] = alertContent;
                     } else if (maskError) {
                        infoObject[key] = maskError;
                     } else if (isRequired) {
                        infoObject[key] = "Required";
                     }
                  });
                  changeFieldsInfo(guid, infoObject);
                  reject();
               } else {
                  resolve(stepResponse);
               }
            } catch {
               reject();
            }
         });
      },
      [formV3Data, changeFieldsInfo],
   );

   const getFormFieldsByGUID = useCallback(
      (formGUID: string): any[] => formV3Responses[formGUID]?.fields ?? [],
      [formV3Responses],
   );

   const value = useMemo(
      () => ({
         formV3Data,
         fieldsInfo,
         changeFieldsInfo,
         setFormV3Data,
         setTableRefreshList,
         tableRefreshList,
         changeFieldValue,
         addNewFormV3Data,
         goToTheNextProcessStep,
         getFormV3Locks,
         getFormFieldsByGUID,
      }),
      [
         formV3Data,
         fieldsInfo,
         changeFieldsInfo,
         setFormV3Data,
         setTableRefreshList,
         tableRefreshList,
         changeFieldValue,
         addNewFormV3Data,
         goToTheNextProcessStep,
         getFormV3Locks,
         getFormFieldsByGUID,
      ],
   );

   return <FormV3Context.Provider value={value}>{children}</FormV3Context.Provider>;
};

export default FormV3Provider;

export const useFormV3 = () => {
   const context = useContext(FormV3Context);
   if (!context)
      throw new Error("You are trying to use FormV3 Context outside the FormV3 Provider");
   return context;
};
